import React from 'react'
import PropTypes from 'prop-types'
import { IconCircleCheck } from '@edulastic/icons'

import { getConfidenceScoreForStandard } from '../utils/AIQuestionAssistance'
import {
  StyledAdditionalStandardsModal,
  StyledSuggestionsList,
  StyledSuggestionRow,
  StyledSuggestionRowHeader,
  StyledSuggestionStatusContainer,
  StyledCurrentlyUsedLabel,
  StyledRemoveAction,
  StyledUseSuggestionButton,
  StyledBrowseFooter,
  StyledBrowseFooterText,
  StyledBrowseStandardsLink,
  StyledStandardIdentifier,
  StyledStandardDescription,
  StyledStandardLowConfidenceWarning,
} from './styled'

const StandardsSuggestedModal = ({
  visible,
  onClose,
  suggestions,
  standardsMetadata,
  selectedStandards,
  onUseSuggestion,
  onRemoveStandard,
  onBrowseStandards,
  lowConfidenceThreshold,
}) => {
  const suggestedStandardIds = new Set(suggestions.map((s) => s._id))
  const userSelectedStandards = selectedStandards.filter(
    (s) => !suggestedStandardIds.has(s._id)
  )
  const rows = [...userSelectedStandards, ...suggestions]

  return (
    <StyledAdditionalStandardsModal
      visible={visible}
      title="Standards Suggested"
      footer={null}
      centered
      onCancel={onClose}
      width={550}
      maskClosable
      destroyOnClose
    >
      <StyledSuggestionsList data-cy="pearAssistStandardSuggestionsList">
        {rows.map((standard) => {
          const isSelected = selectedStandards.some(
            (s) => s._id === standard._id
          )
          const confidence = getConfidenceScoreForStandard(
            standard,
            standardsMetadata
          )
          const showLowConfidence =
            typeof confidence === 'number' &&
            confidence < lowConfidenceThreshold

          return (
            <StyledSuggestionRow
              key={standard._id}
              data-cy={`pearAssistSuggestionRow-${standard.identifier}`}
            >
              <StyledSuggestionRowHeader>
                <StyledStandardIdentifier data-cy="pearAssistSuggestionIdentifier">
                  {standard.identifier}
                </StyledStandardIdentifier>
                <StyledSuggestionStatusContainer>
                  {isSelected ? (
                    <>
                      <StyledCurrentlyUsedLabel data-cy="pearAssistSuggestionCurrentlyUsed">
                        <IconCircleCheck
                          color="#20845e"
                          checkColor="#ffffff"
                          width="16px"
                          height="16px"
                          aria-hidden="true"
                        />
                        Currently used
                      </StyledCurrentlyUsedLabel>
                      <StyledRemoveAction
                        type="button"
                        data-cy="pearAssistSuggestionRemove"
                        onClick={() => onRemoveStandard(standard)}
                      >
                        Remove
                      </StyledRemoveAction>
                    </>
                  ) : (
                    <StyledUseSuggestionButton
                      isGhost
                      data-cy="pearAssistSuggestionUse"
                      onClick={() => onUseSuggestion(standard)}
                    >
                      Use this suggestion
                    </StyledUseSuggestionButton>
                  )}
                </StyledSuggestionStatusContainer>
              </StyledSuggestionRowHeader>
              <StyledStandardDescription data-cy="pearAssistSuggestionDescription">
                {standard.description}
              </StyledStandardDescription>
              {showLowConfidence && (
                <StyledStandardLowConfidenceWarning
                  data-cy="pearAssistSuggestionLowConfidence"
                  role="status"
                >
                  Low confidence on standards suggested
                </StyledStandardLowConfidenceWarning>
              )}
            </StyledSuggestionRow>
          )
        })}
      </StyledSuggestionsList>

      <StyledBrowseFooter>
        <StyledBrowseFooterText>
          Standards not matching, Browse and add standards as per your
          preference.
        </StyledBrowseFooterText>
        <StyledBrowseStandardsLink
          type="button"
          data-cy="pearAssistBrowseStandards"
          onClick={onBrowseStandards}
        >
          + Add Standards
        </StyledBrowseStandardsLink>
      </StyledBrowseFooter>
    </StyledAdditionalStandardsModal>
  )
}

StandardsSuggestedModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  suggestions: PropTypes.array,
  standardsMetadata: PropTypes.array,
  selectedStandards: PropTypes.array,
  onUseSuggestion: PropTypes.func.isRequired,
  onRemoveStandard: PropTypes.func.isRequired,
  onBrowseStandards: PropTypes.func.isRequired,
  lowConfidenceThreshold: PropTypes.number,
}

StandardsSuggestedModal.defaultProps = {
  suggestions: [],
  standardsMetadata: [],
  selectedStandards: [],
  lowConfidenceThreshold: 0.7,
}

export default StandardsSuggestedModal
